export type LiveAvatarSpikeState =
  | 'IDLE'
  | 'REQUESTING_MEDIA'
  | 'READY'
  | 'CONNECTING'
  | 'CONNECTED'
  | 'AVATAR_SPEAKING'
  | 'CANDIDATE_SPEAKING'
  | 'PROCESSING'
  | 'FOLLOW_UP'
  | 'CLOSING'
  | 'COMPLETED'
  | 'RECONNECTING'
  | 'FAILED';

export type LiveAvatarSpikeEvent =
  | 'REQUEST_MEDIA'
  | 'MEDIA_READY'
  | 'MEDIA_FAILED'
  | 'CONNECT'
  | 'CONNECT_SUCCEEDED'
  | 'CONNECT_FAILED'
  | 'AVATAR_STARTED'
  | 'CANDIDATE_STARTED'
  | 'CANDIDATE_FINISHED'
  | 'FOLLOW_UP_ALLOWED'
  | 'CLOSE'
  | 'COMPLETE'
  | 'RECONNECT'
  | 'RECONNECT_SUCCEEDED'
  | 'FAIL'
  | 'RESET';

export type SpikeLatencyInput = {
  connectedAtMs?: number | null;
  connectRequestedAtMs?: number | null;
  firstAvatarRenderAtMs?: number | null;
  candidateSpeechEndedAtMs?: number | null;
  transcriptFinalAtMs?: number | null;
  aiDecisionAtMs?: number | null;
  avatarSpeechStartedAtMs?: number | null;
  reconnectStartedAtMs?: number | null;
  reconnectedAtMs?: number | null;
};

export type SpikeLatencySummary = {
  connectionMs: number | null;
  firstAvatarRenderMs: number | null;
  speechEndToTranscriptFinalMs: number | null;
  transcriptFinalToDecisionMs: number | null;
  decisionToAvatarSpeechMs: number | null;
  fullTurnMs: number | null;
  reconnectMs: number | null;
};

export const LIVE_AVATAR_SPIKE_TRANSITIONS: Record<
  LiveAvatarSpikeState,
  Partial<Record<LiveAvatarSpikeEvent, LiveAvatarSpikeState>>
> = {
  IDLE: { REQUEST_MEDIA: 'REQUESTING_MEDIA', RESET: 'IDLE' },
  REQUESTING_MEDIA: { MEDIA_READY: 'READY', MEDIA_FAILED: 'FAILED', FAIL: 'FAILED', RESET: 'IDLE' },
  READY: { CONNECT: 'CONNECTING', FAIL: 'FAILED', RESET: 'IDLE' },
  CONNECTING: { CONNECT_SUCCEEDED: 'CONNECTED', CONNECT_FAILED: 'FAILED', FAIL: 'FAILED', RESET: 'IDLE' },
  CONNECTED: { AVATAR_STARTED: 'AVATAR_SPEAKING', RECONNECT: 'RECONNECTING', CLOSE: 'CLOSING', FAIL: 'FAILED' },
  AVATAR_SPEAKING: { CANDIDATE_STARTED: 'CANDIDATE_SPEAKING', CLOSE: 'CLOSING', FAIL: 'FAILED' },
  CANDIDATE_SPEAKING: { CANDIDATE_FINISHED: 'PROCESSING', RECONNECT: 'RECONNECTING', FAIL: 'FAILED' },
  PROCESSING: { FOLLOW_UP_ALLOWED: 'FOLLOW_UP', CLOSE: 'CLOSING', FAIL: 'FAILED' },
  FOLLOW_UP: { AVATAR_STARTED: 'AVATAR_SPEAKING', CLOSE: 'CLOSING', FAIL: 'FAILED' },
  CLOSING: { COMPLETE: 'COMPLETED', FAIL: 'FAILED' },
  COMPLETED: { RESET: 'IDLE' },
  RECONNECTING: { RECONNECT_SUCCEEDED: 'CONNECTED', CONNECT_FAILED: 'FAILED', FAIL: 'FAILED' },
  FAILED: { RESET: 'IDLE' },
};

const diffOrNull = (start?: number | null, end?: number | null): number | null =>
  typeof start === 'number' && typeof end === 'number' ? Math.max(0, Math.round(end - start)) : null;

export const transitionLiveAvatarSpikeState = (
  currentState: LiveAvatarSpikeState,
  event: LiveAvatarSpikeEvent
): LiveAvatarSpikeState => LIVE_AVATAR_SPIKE_TRANSITIONS[currentState]?.[event] || currentState;

export const calculateSpikeLatencySummary = (input: SpikeLatencyInput): SpikeLatencySummary => ({
  connectionMs: diffOrNull(input.connectRequestedAtMs, input.connectedAtMs),
  firstAvatarRenderMs: diffOrNull(input.connectRequestedAtMs, input.firstAvatarRenderAtMs),
  speechEndToTranscriptFinalMs: diffOrNull(input.candidateSpeechEndedAtMs, input.transcriptFinalAtMs),
  transcriptFinalToDecisionMs: diffOrNull(input.transcriptFinalAtMs, input.aiDecisionAtMs),
  decisionToAvatarSpeechMs: diffOrNull(input.aiDecisionAtMs, input.avatarSpeechStartedAtMs),
  fullTurnMs: diffOrNull(input.candidateSpeechEndedAtMs, input.avatarSpeechStartedAtMs),
  reconnectMs: diffOrNull(input.reconnectStartedAtMs, input.reconnectedAtMs),
});
